import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditMultiSelectField } from '../../../EditMultiSelectField';
import type {
  AllPotentialInvestorsData,
  ContactSummaryData
} from '../queries/contactSummaryQuery';
import {
  GET_ALL_POTENTIAL_INVESTORS,
  GET_CONTACT_SUMMARY
} from '../queries/contactSummaryQuery';

const UPDATE_CONTACT = gql`
  mutation UpdateContactPotentialInvestors($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        potentialInvestors {
          id
          name
        }
      }
    }
  }
`;

export interface EditPotentialInvestorsDialogProps {
  contactId: string;
  initialValue: ContactSummaryData;
  isOpen: boolean;
  onChange: (data: ContactSummaryData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactSummaryData;
}

export function EditPotentialInvestorsDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditPotentialInvestorsDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  // Fetch all available potential investors
  const { data: potentialInvestorsData, loading: loadingPotentialInvestors } =
    useQuery<AllPotentialInvestorsData>(GET_ALL_POTENTIAL_INVESTORS, {
      fetchPolicy: 'cache-first'
    });

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [
      {
        query: GET_CONTACT_SUMMARY,
        variables: { id: contactId }
      }
    ]
  });

  // Convert API data to options for multi-select
  const options = useMemo((): { label: string; value: string }[] => {
    if (!potentialInvestorsData?.potentialInvestors.edges) return [];
    return potentialInvestorsData.potentialInvestors.edges
      .map(edge => edge.node)
      .filter(node => node.isActive)
      .map(node => ({
        label: node.name,
        value: node.id
      }));
  }, [potentialInvestorsData]);

  // Get selected IDs from value
  const selectedIds = useMemo(
    (): string[] => value.potentialInvestors.map(investor => investor.id),
    [value.potentialInvestors]
  );

  // Check if dirty by comparing IDs
  const initialIds = useMemo(
    (): string[] =>
      initialValue.potentialInvestors.map(investor => investor.id).sort(),
    [initialValue.potentialInvestors]
  );
  const currentIds = useMemo(
    (): string[] => [...selectedIds].sort(),
    [selectedIds]
  );
  const isDirty = JSON.stringify(currentIds) !== JSON.stringify(initialIds);

  const handleChange = (ids: string[]) => {
    // Convert IDs back to ContactNodeLookup objects
    const items = ids.map(id => {
      // Check if it's already in value
      const existing = value.potentialInvestors.find(
        investor => investor.id === id
      );
      if (existing) return existing;
      // Otherwise find from options
      const option = options.find(o => o.value === id);
      return { id, name: option?.label ?? '' };
    });
    onChange({ ...value, potentialInvestors: items });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: {
          id: contactId,
          values: {
            potentialInvestorIds: selectedIds
          }
        }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save potential investors');
      } else {
        toast.success('Potential investors saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving potential investors:', error);
      toast.error('Failed to save potential investors');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Potential Investors"
    >
      <EditMultiSelectField
        label="Potential Investors"
        onChange={handleChange}
        options={options}
        placeholder={
          loadingPotentialInvestors ? 'Loading...' : 'Select investors...'
        }
        values={selectedIds}
      />
    </EditDialog>
  );
}
